import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getMyAvatarBlob, putMyAvatar } from '../../infrastructure/api/usersApi';
import type { AvatarChangeResult, UserProfileDto } from '../../domain/contracts/api.contracts';
import { cooldownRemainingMs } from '../../domain/avatar/prepareAvatar';
import { useAuthStore } from '../stores/authStore';
import { qKeys } from './keys';
import { queryDefaults } from './queryPolicy';

/** Codes the backend puts in the problem body for E34 (API_CONTRACT §1.5). */
export type AvatarErrorCode =
  | 'AVATAR_EMPTY'
  | 'AVATAR_INVALID_TYPE'
  | 'AVATAR_TOO_LARGE'
  | 'AVATAR_CHANGE_COOLDOWN'
  | 'ACCOUNT_SUSPENDED'
  | 'STALE_UPDATE'
  | 'SERVICE_UNAVAILABLE'
  | 'VALIDATION_FAILED';

export interface AvatarFailure {
  /** Undefined = no response at all (offline, DNS, timeout). */
  status?: number;
  code?: AvatarErrorCode;
  /** ISO instant from a 429 `AVATAR_CHANGE_COOLDOWN`; authoritative over the local clock. */
  nextChangeAt?: string;
  /** Server clock (ms since epoch) from the `Date` response header, to correct a skewed client clock. */
  serverNowMs?: number;
}

/** Reads the typed failure out of an axios error. */
export function avatarFailureOf(error: unknown): AvatarFailure {
  const res = (error as { response?: { status?: number; data?: Record<string, unknown>; headers?: Record<string, unknown> } })
    ?.response;
  if (!res) return {};
  const data = res.data ?? {};
  const date = typeof res.headers?.date === 'string' ? new Date(res.headers.date).getTime() : NaN;
  return {
    status: res.status,
    code: typeof data.code === 'string' ? (data.code as AvatarErrorCode) : undefined,
    nextChangeAt: typeof data.nextChangeAt === 'string' ? data.nextChangeAt : undefined,
    serverNowMs: Number.isFinite(date) ? date : undefined,
  };
}

/**
 * E35 — the member's photo as an object URL. The bytes are fetched (with the bearer token) only when the profile
 * says `hasAvatar`, keyed by user id + `avatarUpdatedAt`, so a new version is a new cache entry. The object URL is
 * revoked when the version changes or the component unmounts.
 */
export function useAvatarImage(user: Pick<UserProfileDto, 'id' | 'hasAvatar' | 'avatarUpdatedAt'> | null | undefined) {
  const token = useAuthStore((s) => s.accessToken);
  const enabled = Boolean(user?.hasAvatar) && token !== null;
  const query = useQuery({
    queryKey: qKeys.avatar.image(user?.id ?? '', user?.avatarUpdatedAt),
    queryFn: getMyAvatarBlob,
    ...queryDefaults,
    staleTime: Infinity, // the version in the key changes whenever the photo does
    enabled,
  });
  const blob = enabled ? query.data : undefined;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return { url, isLoading: enabled && query.isPending, isError: enabled && query.isError };
}

/** E34 — upload a prepared photo. Success patches the cached profile, then refetches it and the image. */
export function useChangeAvatar() {
  const queryClient = useQueryClient();
  return useMutation<AvatarChangeResult, unknown, File>({
    mutationFn: (file) => putMyAvatar(file),
    onSuccess: (res) => {
      queryClient.setQueryData<UserProfileDto>(qKeys.profile, (old) =>
        old
          ? { ...old, hasAvatar: true, avatarUpdatedAt: res.avatarUpdatedAt, avatarNextChangeAt: res.avatarNextChangeAt }
          : old,
      );
      void queryClient.invalidateQueries({ queryKey: qKeys.profile });
      void queryClient.invalidateQueries({ queryKey: qKeys.avatar.all });
    },
  });
}

/**
 * Cooldown state for the Change photo button. The profile's `avatarNextChangeAt` is the baseline; a 429 from the
 * server (`applyServer`) overrides it and, via the `Date` header, corrects a skewed client clock, because the
 * server is the only authority on when the next change is allowed.
 */
export function useAvatarCooldown(user: Pick<UserProfileDto, 'avatarNextChangeAt'> | null | undefined, nowMs: number) {
  const [override, setOverride] = useState<{ next: number; skew: number } | null>(null);
  const applyServer = useCallback((failure: AvatarFailure) => {
    if (!failure.nextChangeAt) return;
    const next = new Date(failure.nextChangeAt).getTime();
    if (!Number.isFinite(next)) return;
    setOverride({ next, skew: failure.serverNowMs ? failure.serverNowMs - Date.now() : 0 });
  }, []);
  const clearServer = useCallback(() => setOverride(null), []);

  const profileNext = user?.avatarNextChangeAt ? new Date(user.avatarNextChangeAt).getTime() : null;
  const effectiveNow = nowMs + (override?.skew ?? 0);
  const next = Math.max(profileNext ?? 0, override?.next ?? 0) || null;
  const nextIso = next ? new Date(next).toISOString() : null;
  const remainingMs = cooldownRemainingMs(nextIso, effectiveNow);
  return { nextChangeAt: nextIso, remainingMs, active: remainingMs > 0, applyServer, clearServer };
}
